import { motion, HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/utils";
import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "gold";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  glowOnHover?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = "primary",
    size = "md",
    isLoading,
    leftIcon,
    rightIcon,
    glowOnHover = true,
    children,
    disabled,
    ...props
  }, ref) => {
    const variants = {
      primary: "bg-cream text-deep-black hover:bg-cream-light",
      secondary: "bg-anthracite text-cream border border-cream/20 hover:border-cream/40",
      outline: "bg-transparent border-2 border-gold text-gold hover:bg-gold/10",
      ghost: "bg-transparent text-cream hover:bg-cream/10",
      gold: "bg-gradient-gold text-deep-black hover:shadow-gold-glow",
    };

    const sizes = {
      sm: "px-4 py-2 text-sm",
      md: "px-6 py-3 text-base",
      lg: "px-8 py-4 text-lg",
    };

    return (
      <motion.button
        ref={ref}
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        className={cn(
          "relative inline-flex items-center justify-center gap-2 font-medium rounded-xl transition-all duration-300",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
          variants[variant],
          sizes[size],
          glowOnHover && variant === "gold" && "hover:shadow-gold-glow",
          className
        )}
        disabled={disabled || isLoading}
        {...props as HTMLMotionProps<"button">}
      >
        {/* Loading spinner */}
        {isLoading && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="absolute left-4 w-4 h-4 border-2 border-current border-t-transparent rounded-full"
          />
        )}

        {/* Content */}
        <span className={cn("flex items-center gap-2", isLoading && "opacity-0")}>
          {leftIcon}
          {children}
          {rightIcon}
        </span>
      </motion.button>
    );
  }
);

Button.displayName = "Button";

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  label?: string;
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, variant = "ghost", size = "md", className, ...props }, ref) => {
    const variants = {
      primary: "bg-gold text-deep-black hover:bg-gold-light",
      secondary: "bg-anthracite text-cream border border-cream/20",
      ghost: "bg-transparent text-beige hover:text-cream hover:bg-cream/10",
    };

    const sizes = {
      sm: "w-8 h-8",
      md: "w-10 h-10",
      lg: "w-12 h-12",
    };

    return (
      <motion.button
        ref={ref}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={cn(
          "inline-flex items-center justify-center rounded-xl transition-all duration-200",
          variants[variant],
          sizes[size],
          className
        )}
        title={label}
        {...props as HTMLMotionProps<"button">}
      >
        {icon}
      </motion.button>
    );
  }
);

IconButton.displayName = "IconButton";

export default { Button, IconButton };
